import { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config/env';
import { createApiKey, deleteApiKey, fetchApiKeys, revokeApiKey } from '../api/apiKeys';
import { buildCrmSwalBody, fireConfirmDialog, showApiKeyGeneratedDialog } from '../utils/swalConfirm';
import '../styles/conversaciones-premium.css';
import '../styles/gestion-derivacion-premium.css';
import '../styles/configuracion-premium.css';

const API_KEY_EXPIRY_OPTIONS = [
  { value: 30, label: '30 días' },
  { value: 90, label: '90 días' },
  { value: 180, label: '6 meses' },
  { value: 365, label: '1 año' },
  { value: 0, label: 'Sin vencimiento' }
];

function formatDate(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (_) {
    return String(value);
  }
}

function formatDateShort(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('es-AR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  } catch (_) {
    return String(value);
  }
}

function resolveKeyStatus(row) {
  if (row?.is_revoked) return 'revoked';
  if (row?.is_expired) return 'expired';
  if (row?.is_active) return 'active';
  return 'inactive';
}

function ApiKeyStatusBadge({ row }) {
  const status = resolveKeyStatus(row);
  const labels = {
    active: 'Activa',
    expired: 'Expirada',
    revoked: 'Revocada',
    inactive: 'Inactiva'
  };
  return (
    <span className={`crm-cfg-api-item__badge crm-cfg-api-item__badge--${status}`}>
      {labels[status]}
    </span>
  );
}

function ApiKeyIconRevoke() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.75" />
      <path d="M7.5 7.5l9 9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function ApiKeyIconTrash() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 7h14M10 7V5.8a.8.8 0 0 1 .8-.8h2.4a.8.8 0 0 1 .8.8V7M8.5 7l.7 11.2h5.6L15.5 7"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ApiKeyIconCopy() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="8.5" y="8.5" width="10" height="10" rx="1.8" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M8 15.5H6.8a1.8 1.8 0 0 1-1.8-1.8V6.8A1.8 1.8 0 0 1 6.8 5h6.9a1.8 1.8 0 0 1 1.8 1.8V8"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ApiKeyExpiryPanel({ row }) {
  if (row.never_expires || !row.expires_at) {
    return (
      <div className="crm-cfg-api-expiry crm-cfg-api-expiry--never">
        <div className="crm-cfg-api-expiry__head">
          <span className="crm-cfg-api-expiry__label">Vencimiento</span>
          <strong className="crm-cfg-api-expiry__date">Sin vencimiento</strong>
        </div>
        <p className="crm-cfg-api-expiry__hint">Esta clave no expira automáticamente.</p>
      </div>
    );
  }

  const status = resolveKeyStatus(row);
  const percent = Number.isFinite(row.lifetime_percent)
    ? Math.min(100, Math.max(0, row.lifetime_percent))
    : 0;
  const daysLeft = Number(row.days_remaining ?? 0);
  const urgency = status === 'expired'
    ? 'expired'
    : daysLeft <= 7
      ? 'critical'
      : daysLeft <= 30
        ? 'warning'
        : 'ok';

  let hint = `Quedan ${daysLeft} día${daysLeft === 1 ? '' : 's'}`;
  if (status === 'expired') hint = 'Venció y ya no acepta requests';
  if (status === 'revoked') hint = 'Revocada manualmente';

  return (
    <div className={`crm-cfg-api-expiry crm-cfg-api-expiry--${urgency}`}>
      <div className="crm-cfg-api-expiry__head">
        <span className="crm-cfg-api-expiry__label">Vence</span>
        <strong className="crm-cfg-api-expiry__date">{formatDateShort(row.expires_at)}</strong>
      </div>
      <div className="crm-cfg-api-expiry__track" aria-hidden>
        <span className="crm-cfg-api-expiry__fill" style={{ width: `${percent}%` }} />
      </div>
      <p className="crm-cfg-api-expiry__hint">{hint}</p>
    </div>
  );
}

function AdminMaintenancePanel() {
  const { syncMaintenanceStatus } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [lastUpdated, setLastUpdated] = useState('');
  const [error, setError] = useState('');

  const statusEndpoint = useMemo(() => {
    const base = String(API_BASE_URL || '').replace(/\/+$/, '');
    return `${base}/api/admin/frontend/status`;
  }, []);

  const isMaintenanceMode = !isActive;
  const busy = loading || saving;

  const loadStatus = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(statusEndpoint, { credentials: 'include' });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'No se pudo consultar el estado.');
      setIsActive(Boolean(json?.isActive));
      setLastUpdated(new Date().toLocaleString('es-AR'));
    } catch (e) {
      setError(e.message || 'No se pudo consultar el estado.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (nextIsActive) => {
    const wantMaintenance = !nextIsActive;
    const confirm = await fireConfirmDialog({
      title: wantMaintenance ? 'Modo mantenimiento' : 'Reactivar frontend',
      html: buildCrmSwalBody({
        lead: wantMaintenance
          ? 'El login mostrará “Sistema en mantenimiento”.'
          : 'Los usuarios podrán iniciar sesión con normalidad.',
        items: wantMaintenance
          ? ['No se aceptarán credenciales', 'Solo administradores pueden revertir']
          : ['Acceso normal al CRM', 'Sin mensaje de mantenimiento']
      }),
      icon: wantMaintenance ? 'warning' : 'question',
      confirmButtonText: wantMaintenance ? 'Activar' : 'Reactivar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirm.isConfirmed) return;

    setSaving(true);
    setError('');
    try {
      const res = await fetch(statusEndpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: nextIsActive })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.error || 'No se pudo actualizar el estado.');
      const active = Boolean(json?.isActive);
      setIsActive(active);
      setLastUpdated(new Date().toLocaleString('es-AR'));
      await syncMaintenanceStatus(active);
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: 'Estado actualizado',
        text: json?.isActive ? 'Frontend activo nuevamente.' : 'Frontend en modo mantenimiento.',
        timer: 2200,
        timerProgressBar: true,
        showConfirmButton: false,
        customClass: { popup: 'swal-toast-crm' }
      });
    } catch (e) {
      setError(e.message || 'No se pudo actualizar el estado.');
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e.message || 'No se pudo actualizar el estado.'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="crm-gd-loading" role="status" aria-live="polite" aria-busy="true">
        <div className="crm-gd-spinner" aria-hidden="true" />
        <div className="crm-gd-loading__title">Cargando mantenimiento</div>
        <p className="crm-cfg-loading-sub">Consultando estado del frontend…</p>
      </div>
    );
  }

  return (
    <>
      <div className="crm-gd-kpi-grid crm-cfg-kpi-grid--compact">
        <div className="crm-gd-kpi-card">
          <div
            className={`crm-gd-kpi-card__num ${
              isMaintenanceMode ? 'is-maintenance' : 'is-active'
            }`}
          >
            {isMaintenanceMode ? 'Mantenimiento' : 'Activo'}
          </div>
          <div className="crm-gd-kpi-card__label">Estado del login</div>
        </div>
        <div className="crm-gd-kpi-card">
          <div className="crm-gd-kpi-card__num is-meta">
            {lastUpdated || '—'}
          </div>
          <div className="crm-gd-kpi-card__label">Última actualización</div>
        </div>
      </div>

      {error ? (
        <div className="crm-cfg-alert" role="alert">
          {error}
        </div>
      ) : null}

      <div className="crm-gd-card crm-cfg-card">
        <div className="crm-cfg-card__head">
          <h2>Mantenimiento del frontend</h2>
          <p>
            Gestioná si el login está habilitado o responde con estado de mantenimiento.
          </p>
        </div>
        <div className="crm-cfg-card__body">
          <p className="crm-cfg-card__hint">
            {isMaintenanceMode
              ? 'El CRM está en mantenimiento: los usuarios verán el aviso en el login y no podrán ingresar con credenciales.'
              : 'El CRM está activo: los usuarios pueden iniciar sesión con normalidad.'}
          </p>
          <div className="crm-cfg-actions">
            <button
              type="button"
              className="crm-gd-btn crm-gd-btn--ghost"
              onClick={() => void loadStatus()}
              disabled={busy}
            >
              Recargar
            </button>
            <button
              type="button"
              className="crm-gd-btn crm-gd-btn--danger"
              onClick={() => void updateStatus(false)}
              disabled={busy || isMaintenanceMode}
            >
              Activar mantenimiento
            </button>
            <button
              type="button"
              className="crm-gd-btn crm-gd-btn--success"
              onClick={() => void updateStatus(true)}
              disabled={busy || !isMaintenanceMode}
            >
              Reactivar CRM
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function AdminApiKeysPanel() {
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState('');
  const [expiresInDays, setExpiresInDays] = useState(90);
  const [error, setError] = useState('');
  const [revealedKey, setRevealedKey] = useState('');

  const apiBase = useMemo(() => String(API_BASE_URL || '').replace(/\/+$/, ''), []);

  const activeKeys = useMemo(
    () => keys.filter((k) => resolveKeyStatus(k) === 'active'),
    [keys]
  );

  const expiringSoon = useMemo(
    () => keys.filter((k) => {
      const status = resolveKeyStatus(k);
      return status === 'active' && !k.never_expires && Number(k.days_remaining) <= 30;
    }),
    [keys]
  );

  const loadKeys = async () => {
    setLoading(true);
    setError('');
    try {
      const json = await fetchApiKeys();
      setKeys(Array.isArray(json?.data) ? json.data : []);
    } catch (e) {
      setError(e.message || 'No se pudieron cargar las API keys.');
      setKeys([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const resolveCopyableKey = (row) => {
    if (revealedKey && row.key_prefix && revealedKey.startsWith(row.key_prefix)) {
      return { text: revealedKey, label: 'API key copiada', full: true };
    }
    const prefix = row.key_prefix || String(row.mask || '').replace(/…$/, '');
    return { text: prefix, label: 'Prefijo copiado', full: false };
  };

  const handleCopyKey = async (row) => {
    const { text, label } = resolveCopyableKey(row);
    if (!text) return;
    await copyText(text, label);
  };

  const copyText = async (text, label = 'Copiado') => {
    try {
      await navigator.clipboard.writeText(text);
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: label,
        timer: 1800,
        timerProgressBar: true,
        showConfirmButton: false,
        customClass: { popup: 'swal-toast-crm' }
      });
    } catch (_) {
      Swal.fire({ icon: 'error', title: 'No se pudo copiar', text: 'Copiá manualmente el valor.' });
    }
  };

  const handleCreate = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Ingresá un nombre para identificar la API key.');
      return;
    }

    setCreating(true);
    setError('');
    try {
      const json = await createApiKey(trimmed, expiresInDays);
      const created = json?.data;
      setName('');
      setRevealedKey(created?.api_key || '');
      await loadKeys();

      const expiryLabel = created?.never_expires
        ? 'Sin vencimiento'
        : formatDateShort(created?.expires_at);

      if (created?.api_key) {
        const result = await showApiKeyGeneratedDialog({
          apiKey: created.api_key,
          expiryLabel
        });
        if (result.isConfirmed) {
          await copyText(created.api_key, 'API key copiada');
        }
      }
    } catch (e) {
      setError(e.message || 'No se pudo crear la API key.');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (row) => {
    const confirm = await fireConfirmDialog({
      title: 'Revocar API key',
      html: buildCrmSwalBody({
        lead: `¿Revocar «${row.name}»?`,
        items: [
          'Dejará de funcionar de inmediato',
          'Las integraciones que la usen fallarán con 401',
          'No se puede deshacer'
        ]
      }),
      icon: 'warning',
      confirmButtonText: 'Revocar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirm.isConfirmed) return;

    setRevokingId(row.id);
    setError('');
    try {
      await revokeApiKey(row.id);
      if (revealedKey && row.key_prefix && revealedKey.startsWith(row.key_prefix)) {
        setRevealedKey('');
      }
      await loadKeys();
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: 'API key revocada',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        customClass: { popup: 'swal-toast-crm' }
      });
    } catch (e) {
      setError(e.message || 'No se pudo revocar la API key.');
    } finally {
      setRevokingId(null);
    }
  };

  const handleDelete = async (row) => {
    const confirm = await fireConfirmDialog({
      title: 'Eliminar API key',
      html: buildCrmSwalBody({
        lead: `¿Eliminar permanentemente «${row.name}»?`,
        items: [
          'Se borra del sistema y desaparece del listado',
          'Si estaba activa, dejará de funcionar al instante',
          'Esta acción no se puede deshacer'
        ]
      }),
      icon: 'warning',
      confirmButtonText: 'Eliminar',
      cancelButtonText: 'Cancelar'
    });
    if (!confirm.isConfirmed) return;

    setDeletingId(row.id);
    setError('');
    try {
      await deleteApiKey(row.id);
      if (revealedKey && row.key_prefix && revealedKey.startsWith(row.key_prefix)) {
        setRevealedKey('');
      }
      await loadKeys();
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: 'API key eliminada',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        customClass: { popup: 'swal-toast-crm' }
      });
    } catch (e) {
      setError(e.message || 'No se pudo eliminar la API key.');
    } finally {
      setDeletingId(null);
    }
  };

  const curlExample = `curl -H "X-API-Key: pk_live_..." \\
  "${apiBase}/api/asociacion/..."`;

  return (
    <>
      <div className="crm-gd-kpi-grid crm-cfg-kpi-grid--compact crm-cfg-kpi-grid--api">
        <div className="crm-gd-kpi-card">
          <div className="crm-gd-kpi-card__num is-active">{activeKeys.length}</div>
          <div className="crm-gd-kpi-card__label">Activas</div>
        </div>
        <div className="crm-gd-kpi-card">
          <div className="crm-gd-kpi-card__num is-warning">{expiringSoon.length}</div>
          <div className="crm-gd-kpi-card__label">Por vencer (30 d)</div>
        </div>
        <div className="crm-gd-kpi-card">
          <div className="crm-gd-kpi-card__num is-meta">{keys.length}</div>
          <div className="crm-gd-kpi-card__label">Total</div>
        </div>
      </div>

      {error ? (
        <div className="crm-cfg-alert" role="alert">
          {error}
        </div>
      ) : null}

      <div className="crm-gd-card crm-cfg-card crm-cfg-card--apikeys">
        <div className="crm-cfg-card__head">
          <h2>API keys</h2>
          <p>
            Generá claves para que bots, n8n u otros servicios consuman la API sin sesión de usuario.
          </p>
        </div>
        <div className="crm-cfg-card__body">
          <div className="crm-cfg-api-create">
            <label className="crm-cfg-api-create__field">
              <span>Nombre de la clave</span>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Nombre Api Key"
                maxLength={120}
                disabled={creating}
              />
            </label>
            <label className="crm-cfg-api-create__field crm-cfg-api-create__field--expiry">
              <span>Vigencia</span>
              <select
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                disabled={creating}
              >
                {API_KEY_EXPIRY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="crm-gd-btn crm-gd-btn--primary crm-cfg-api-create__btn"
              onClick={() => void handleCreate()}
              disabled={creating}
            >
              {creating ? 'Generando…' : '+ Generar API key'}
            </button>
          </div>

          <div className="crm-cfg-api-usage">
            <p className="crm-cfg-api-usage__title">Cómo usarla</p>
            <p className="crm-cfg-api-usage__text">
              Enviá el header <code>X-API-Key</code> o <code>Authorization: Bearer &lt;clave&gt;</code>
              {' '}en cada request a rutas como <code>/api/asociacion</code> y <code>/api/asociacion-recetas</code>.
            </p>
            <pre className="crm-cfg-api-usage__code">{curlExample}</pre>
            <button
              type="button"
              className="crm-cfg-api-usage__copy"
              onClick={() => void copyText(curlExample, 'Ejemplo copiado')}
            >
              Copiar ejemplo
            </button>
          </div>

          {loading ? (
            <div className="crm-gd-loading crm-cfg-api-loading" role="status">
              <div className="crm-gd-spinner" aria-hidden="true" />
              <span>Cargando claves…</span>
            </div>
          ) : !keys.length ? (
            <p className="crm-cfg-card__hint">Todavía no hay API keys. Generá la primera para integrar servicios externos.</p>
          ) : (
            <div className="crm-cfg-api-list" role="list">
              {keys.map((row) => {
                const status = resolveKeyStatus(row);
                const busyRow = revokingId === row.id || deletingId === row.id;
                return (
                  <article
                    key={row.id}
                    className={`crm-cfg-api-item crm-cfg-api-item--${status}`}
                    role="listitem"
                  >
                    <div className="crm-cfg-api-item__main">
                      <div className="crm-cfg-api-item__top">
                        <h3 className="crm-cfg-api-item__name">{row.name}</h3>
                        <ApiKeyStatusBadge row={row} />
                        <div className="crm-cfg-api-item__icon-actions">
                          <button
                            type="button"
                            className="crm-cfg-api-item__icon-btn crm-cfg-api-item__icon-btn--copy"
                            onClick={() => void handleCopyKey(row)}
                            title={
                              revealedKey && row.key_prefix && revealedKey.startsWith(row.key_prefix)
                                ? 'Copiar API key completa'
                                : 'Copiar prefijo visible'
                            }
                            aria-label="Copiar clave"
                          >
                            <ApiKeyIconCopy />
                          </button>
                          <button
                            type="button"
                            className="crm-cfg-api-item__icon-btn crm-cfg-api-item__icon-btn--revoke"
                            onClick={() => void handleRevoke(row)}
                            disabled={busyRow || status !== 'active'}
                            title={status === 'active' ? 'Revocar' : 'Ya no está activa'}
                            aria-label="Revocar API key"
                          >
                            <ApiKeyIconRevoke />
                          </button>
                          <button
                            type="button"
                            className="crm-cfg-api-item__icon-btn crm-cfg-api-item__icon-btn--delete"
                            onClick={() => void handleDelete(row)}
                            disabled={busyRow}
                            title="Eliminar"
                            aria-label="Eliminar API key"
                          >
                            <ApiKeyIconTrash />
                          </button>
                        </div>
                      </div>
                      <code className="crm-cfg-api-item__mask">{row.mask || `${row.key_prefix}…`}</code>
                      <ApiKeyExpiryPanel row={row} />
                      <div className="crm-cfg-api-item__meta">
                        <span>Creada {formatDate(row.created_at)}</span>
                        <span>Último uso {formatDate(row.last_used_at)}</span>
                        {row.revoked_at ? (
                          <span>Revocada {formatDate(row.revoked_at)}</span>
                        ) : null}
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function Configuracion() {
  const { user, maintenanceActive } = useAuth();
  const isAdmin = Boolean(user?.isAdmin);
  const isMaintenanceMode = Boolean(maintenanceActive);

  if (!isAdmin) {
    return (
      <section className="crm-gd-page crm-cfg-page crm-pbody">
        <p className="crm-cfg-unauthorized">No autorizado.</p>
      </section>
    );
  }

  return (
    <section className="crm-gd-page crm-cfg-page crm-pbody">
      <div className="crm-gd-toolbar crm-cfg-toolbar">
        <div className="crm-cfg-toolbar__lead">
          <h1 className="crm-cfg-toolbar__title">Configuración</h1>
          <p className="crm-cfg-toolbar__sub">
            Mantenimiento del CRM, API keys e integraciones externas.
          </p>
        </div>
        <span
          className={`crm-cfg-status ${
            isMaintenanceMode ? 'crm-cfg-status--maintenance' : 'crm-cfg-status--active'
          }`}
        >
          {isMaintenanceMode ? '🛠 Mantenimiento activo' : '✅ Panel activo'}
        </span>
      </div>

      <div className="crm-cfg-sections">
        <section className="crm-cfg-section">
          <header className="crm-cfg-section__head">
            <span className="crm-cfg-section__icon" aria-hidden>🛠</span>
            <div>
              <h2 className="crm-cfg-section__title">Operación</h2>
              <p className="crm-cfg-section__sub">Estado del login y mantenimiento del frontend.</p>
            </div>
          </header>
          <AdminMaintenancePanel />
        </section>

        <section className="crm-cfg-section">
          <header className="crm-cfg-section__head">
            <span className="crm-cfg-section__icon" aria-hidden>🔐</span>
            <div>
              <h2 className="crm-cfg-section__title">Acceso API</h2>
              <p className="crm-cfg-section__sub">Claves para bots, n8n y servicios sin sesión web.</p>
            </div>
          </header>
          <AdminApiKeysPanel />
        </section>
      </div>
    </section>
  );
}
